<template>
  <div class="bg-white border border-gray-200 rounded-lg p-3 sm:p-6 mb-6">
    <h4 class="font-bold text-black mb-4">
      {{ title || "Préparation du groupage" }}
    </h4>

    <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
      <div class="flex items-center justify-between mb-4">
        <h5 class="font-semibold text-black">
          <i class="fas fa-ruler-combined mr-2 text-blue-600"></i>
          Dimensions des colis
        </h5>
      </div>

      <p class="text-sm text-black mb-4">
        Veuillez renseigner les dimensions en centimètres (cm) et le poids pour
        chaque colis. Tous les champs sont obligatoires. Si un colis dépasse le
        gabarit indiqué, répartissez son contenu dans deux colis ou plus.
      </p>
      <p v-if="dimensionLimitsHint" class="text-xs font-medium text-black mb-2">
        {{ dimensionLimitsHint }}
      </p>

      <div id="ContainerInputColis" class="space-y-4">
        <div
          v-for="(colis, index) in colisList"
          :key="`colis-${index}-${colisList.length}`"
          class="divColis bg-white border border-gray-300 rounded-lg p-3 sm:p-4"
        >
          <div class="flex items-center justify-between mb-3">
            <span class="font-medium text-black">
              Colis n°{{ index + 1 }}
            </span>
            <button
              v-if="index > 0 && canEdit"
              @click.stop="removeColis(index)"
              :disabled="isSaving"
              class="px-3 py-1.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 disabled:bg-gray-200 disabled:text-black disabled:cursor-not-allowed transition-colors text-sm font-medium"
              type="button"
              title="Supprimer ce colis"
            >
              <i
                class="fas fa-trash-alt mr-1"
                :class="{ 'fa-spin': isSaving }"
              ></i>
              {{ isSaving ? "Suppression..." : "Supprimer" }}
            </button>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label
                :for="`colis-${index}-longueur`"
                class="block text-sm font-medium text-black mb-1"
              >
                Longueur (cm) <span class="text-red-600">*</span>
              </label>
              <input
                :id="`colis-${index}-longueur`"
                :value="colis.longueur"
                @input="(e) => handleDimensionChange(index, 'longueur', e)"
                @keydown="blockNonNumeric"
                @paste="handlePaste"
                @wheel="(e) => (e.target as HTMLInputElement).blur()"
                type="number"
                class="numbersonly longueur w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                :class="dimensionFieldClass(colis)"
                placeholder="longueur"
                :readonly="!canEdit"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label
                :for="`colis-${index}-largeur`"
                class="block text-sm font-medium text-black mb-1"
              >
                Largeur (cm) <span class="text-red-600">*</span>
              </label>
              <input
                :id="`colis-${index}-largeur`"
                :value="colis.largeur"
                @input="(e) => handleDimensionChange(index, 'largeur', e)"
                @keydown="blockNonNumeric"
                @paste="handlePaste"
                @wheel="(e) => (e.target as HTMLInputElement).blur()"
                type="number"
                class="numbersonly largeur w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                :class="dimensionFieldClass(colis)"
                placeholder="largeur"
                :readonly="!canEdit"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label
                :for="`colis-${index}-hauteur`"
                class="block text-sm font-medium text-black mb-1"
              >
                Hauteur (cm) <span class="text-red-600">*</span>
              </label>
              <input
                :id="`colis-${index}-hauteur`"
                :value="colis.hauteur"
                @input="(e) => handleDimensionChange(index, 'hauteur', e)"
                @keydown="blockNonNumeric"
                @paste="handlePaste"
                @wheel="(e) => (e.target as HTMLInputElement).blur()"
                type="number"
                class="numbersonly hauteur w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                :class="dimensionFieldClass(colis)"
                placeholder="hauteur"
                :readonly="!canEdit"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label
                :for="`colis-${index}-poids`"
                class="block text-sm font-medium text-black mb-1"
              >
                Poids (kg) <span class="text-red-600">*</span>
              </label>
              <input
                :id="`colis-${index}-poids`"
                :value="colis.poids"
                @input="(e) => handleDimensionChange(index, 'poids', e)"
                @keydown="blockNonNumeric"
                @paste="handlePaste"
                @wheel="(e) => (e.target as HTMLInputElement).blur()"
                type="number"
                class="numbersonly poids w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                :class="dimensionFieldClass(colis, true)"
                placeholder="poids"
                :readonly="!canEdit"
                min="0"
                :max="dimensionLimits.poids"
                step="0.01"
              />
            </div>
          </div>
          <p
            v-if="parcelError(colis)"
            class="mt-3 text-sm font-medium text-red-600"
          >
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ parcelError(colis) }}
          </p>
        </div>
      </div>

      <ParcelWeightWarning
        show-comparison
        :calculated-weight="calculatedWeight"
        :parcels="colisList"
      />

      <div class="mt-4 flex gap-3">
        <button
          v-if="canEdit"
          @click="addColis"
          :disabled="isSaving"
          class="px-4 py-2 border border-blue-500 rounded-md text-blue-600 hover:border-blue-700 hover:text-blue-700 transition-colors disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed text-sm font-medium"
          type="button"
        >
          <i class="fas fa-plus mr-2" :class="{ 'fa-spin': isSaving }"></i>
          {{ isSaving ? "Sauvegarde..." : "Ajouter un colis" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useCustomsDeclarationStore } from "@/stores/customsDeclaration";
import ParcelWeightWarning from "@/components/order/ParcelWeightWarning.vue";
import { useNotification } from "@/composables/useNotification";
import {
  getCarrierDimensionLimits,
  getDimensionLimitsHint,
  validateColisDimensions,
} from "@/utils/parcelDimensionsLimits";

const props = defineProps<{
  orderId: number;
  orderStatus: number;
  nbColis: number;
  idLivraison?: number | null;
  countryCode?: string | null;
  postalCode?: string | null;
  title?: string;
  calculatedWeight?: number | null;
}>();

const emit = defineEmits<{
  "update:valid": [value: boolean];
  "colis-count-changed": [value: number, removedIndex?: number];
  "weight-changed": [index: number, value: number | null];
}>();

// Utiliser le store Pinia étendu
const customsStore = useCustomsDeclarationStore();

// État dérivé du store
const colisList = computed(() =>
  customsStore.getParcelDimensions(props.orderId),
);
const isSaving = ref(false);
const { error: showError } = useNotification();
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

const dimensionLimitsHint = computed(() =>
  getDimensionLimitsHint(props.idLivraison ?? null, props.countryCode, props.postalCode),
);
const dimensionLimits = computed(() =>
  getCarrierDimensionLimits(props.idLivraison ?? null, props.countryCode, props.postalCode),
);
const dimensionFieldClass = (
  colis: (typeof colisList.value)[number],
  isWeight = false,
) =>
  isWeight
    ? colis.poids != null && colis.poids > dimensionLimits.value.poids
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:ring-blue-500"
    : colis.longueur != null &&
        colis.largeur != null &&
        colis.hauteur != null &&
        validateColisDimensions(
          colis.longueur,
          colis.largeur,
          colis.hauteur,
          dimensionLimits.value,
        )
    ? "border-red-500 focus:ring-red-500"
    : "border-gray-300 focus:ring-blue-500";
const parcelError = (colis: (typeof colisList.value)[number]) => {
  const { longueur, largeur, hauteur, poids } = colis;
  if (
    longueur == null ||
    largeur == null ||
    hauteur == null ||
    poids == null
  )
    return "Toutes les dimensions et le poids sont obligatoires.";
  if (longueur <= 0 || largeur <= 0 || hauteur <= 0 || poids <= 0)
    return "Toutes les dimensions et le poids doivent être supérieurs à zéro.";
  const dimensionsError = validateColisDimensions(
    longueur,
    largeur,
    hauteur,
    dimensionLimits.value,
  );
  if (dimensionsError) return `Gabarit dépassé : ${dimensionsError}.`;
  if (poids > dimensionLimits.value.poids)
    return `Poids ${poids} kg (maximum ${dimensionLimits.value.poids} kg).`;
  return "";
};
// Éditable en attente (1/9), confirmée (4) et pickup groupage (7/8)
const canEdit = computed(() => {
  const status = Number(props.orderStatus);
  return status === 1 || status === 9 || status === 4 || status === 7 || status === 8;
});

// Validation bloquante : champs obligatoires et gabarit eFashion respecté.
const dimensionsValid = computed(() => {
  const list = colisList.value;
  if (!list || list.length === 0) return false;
  const limits = dimensionLimits.value;
  return list.every((colis) => {
    const l = colis.longueur;
    const w = colis.largeur;
    const h = colis.hauteur;
    const p = colis.poids;
    if (
      l == null ||
      l <= 0 ||
      w == null ||
      w <= 0 ||
      h == null ||
      h <= 0 ||
      p == null ||
      p <= 0
    )
      return false;
    return (
      p <= limits.poids && validateColisDimensions(l, w, h, limits) === null
    );
  });
});

const addColis = async () => {
  await customsStore.addParcel(props.orderId);
  emit("colis-count-changed", colisList.value.length);
};

const removeColis = async (index: number) => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    await customsStore.removeParcel(props.orderId, index);
    emit("colis-count-changed", colisList.value.length, index);
  } catch (error) {
    showError(error instanceof Error ? error.message : "Erreur lors de la suppression");
  } finally {
    isSaving.value = false;
  }
};

const blockNonNumeric = (event: KeyboardEvent) => {
  const allowed = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (allowed.includes(event.key)) return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(event.key)
  )
    return;
  if (event.key === "." || event.key === ",") return;
  if (event.key < "0" || event.key > "9") {
    event.preventDefault();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  const paste = event.clipboardData?.getData("text") || "";
  if (!/^\d*\.?\d*$/.test(paste)) {
    event.preventDefault();
  }
};

const handleDimensionChange = (
  index: number,
  dimension: "longueur" | "largeur" | "hauteur" | "poids",
  event: Event,
) => {
  const target = event.target as HTMLInputElement | null;
  if (target) {
    const value = target.value ? Number(target.value) : null;
    customsStore.updateParcelDimension(props.orderId, index, dimension, value);
    if (dimension === "poids") emit("weight-changed", index, value);
  }
};

// Initialiser le store au montage
onMounted(() => {
  // Si pas de dimensions dans le store, les initialiser
  if (!customsStore.hasParcelDimensions(props.orderId)) {
    customsStore.initializeParcelDimensions(props.orderId, props.nbColis || 1);
  }
});

// Réinitialiser si les props changent
watch(
  [() => props.orderId, () => props.orderStatus],
  ([newOrderId, newOrderStatus]) => {
    if (newOrderId && newOrderStatus !== undefined) {
      if (!customsStore.hasParcelDimensions(newOrderId)) {
        customsStore.initializeParcelDimensions(newOrderId, props.nbColis || 1);
      }
    }
  },
);

watch(
  dimensionsValid,
  (val) => {
    emit("update:valid", val);
  },
  { immediate: true },
);

watch(
  () => colisList.value.map(({ longueur, largeur, hauteur, poids }) => [
    longueur,
    largeur,
    hauteur,
    poids,
  ]),
  () => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      autoSaveTimer = null;
    }
    if (!canEdit.value || !dimensionsValid.value) return;
    autoSaveTimer = setTimeout(async () => {
      autoSaveTimer = null;
      isSaving.value = true;
      try {
        await customsStore.saveDimensionsToDB(props.orderId);
      } catch (error) {
        showError(error instanceof Error ? error.message : "Impossible d’enregistrer les dimensions");
      } finally {
        isSaving.value = false;
      }
    }, 500);
  },
  { deep: true },
);

onBeforeUnmount(() => {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
});
</script>

<style scoped>
.numbersonly {
  -moz-appearance: textfield;
  appearance: textfield;
}

.numbersonly::-webkit-outer-spin-button,
.numbersonly::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
</style>
